// ═══════════════════════════════════════════════════════
// SKELETON LOADING COMPONENTS
// Brand-aligned: uses design tokens, dark-native
// CSS animation injected via globalStyles.js
// ═══════════════════════════════════════════════════════
import type { CSSProperties } from "react";
import { type ThemeObject, DARK, RADIUS, SP } from "./theme.js";

interface SkeletonBoxProps {
  w?: string | number;
  h?: number;
  br?: string | number;
  mb?: number;
  dark?: boolean;
  style?: CSSProperties;
}

/**
 * SkeletonBox — single shimmer placeholder.
 * Uses .lpa-skeleton / .lpa-skeleton-dark classes from globalStyles.
 */
export function SkeletonBox({
  w = "100%",
  h = 16,
  br,
  mb = 0,
  dark = false,
  style = {},
}: SkeletonBoxProps) {
  return (
    <div
      className={dark ? "lpa-skeleton-dark" : "lpa-skeleton"}
      aria-hidden="true"
      style={{
        width: w,
        height: h,
        borderRadius: br ?? RADIUS.sm,
        marginBottom: mb,
        flexShrink: 0,
        ...style,
      }}
    />
  );
}

interface SkeletonPulseProps extends SkeletonBoxProps {
  width?: string | number;
  height?: number;
}

/** Alias with width/height props for convenience */
export function SkeletonPulse({ width = "100%", height = 16, ...rest }: SkeletonPulseProps) {
  return <SkeletonBox w={width} h={height} {...rest} />;
}

interface SkeletonCardProps {
  lines?: number;
  dark?: boolean;
  T?: ThemeObject;
}

/**
 * SkeletonCard — card placeholder with multiple lines.
 */
export function SkeletonCard({ lines = 3, dark = false, T }: SkeletonCardProps) {
  const isDark = T === DARK || dark;
  return (
    <div
      aria-hidden="true"
      role="status"
      aria-label="Carregando..."
      style={{
        background: T?.card || (isDark ? "#1E293B" : "white"),
        borderRadius: RADIUS.lg,
        padding: SP[6],
        boxShadow: isDark ? "0 2px 8px rgba(0,0,0,0.16)" : "0 1px 6px rgba(0,0,0,0.07)",
        border: isDark ? "1px solid rgba(255,255,255,0.06)" : "none",
      }}
    >
      <SkeletonBox h={18} w="55%" mb={12} dark={isDark} />
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBox key={i} h={12} w={i === lines - 1 ? "65%" : "90%"} mb={8} dark={isDark} />
      ))}
    </div>
  );
}

interface SkeletonMetricRowProps {
  dark?: boolean;
  T?: ThemeObject;
  count?: number;
}

/**
 * SkeletonMetricRow — row of 3 metric card placeholders.
 */
export function SkeletonMetricRow({ dark = false, T, count = 3 }: SkeletonMetricRowProps) {
  const isDark = T === DARK || dark;
  return (
    <div
      aria-hidden="true"
      role="status"
      aria-label="Carregando métricas..."
      className="lpa-metric-grid"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fill,minmax(220px,1fr))`,
        gap: parseInt(SP[4] as string),
        marginBottom: parseInt(SP[4] as string),
      }}
    >
      {Array.from({ length: count }).map((_, idx) => (
        <div
          key={idx}
          style={{
            background: T?.card || (isDark ? "#1E293B" : "white"),
            borderRadius: RADIUS.lg,
            padding: SP[5],
            border: isDark ? "1px solid rgba(255,255,255,0.06)" : "none",
          }}
        >
          <SkeletonBox h={11} w="50%" mb={10} dark={isDark} />
          <SkeletonBox h={28} w="70%" mb={6} dark={isDark} />
          <SkeletonBox h={11} w="40%" dark={isDark} />
        </div>
      ))}
    </div>
  );
}

interface SkeletonTableProps {
  rows?: number;
  cols?: number;
  dark?: boolean;
  T?: ThemeObject;
}

/**
 * SkeletonTable — placeholder for table loading states.
 */
export function SkeletonTable({ rows = 5, cols = 4, dark = false, T }: SkeletonTableProps) {
  const isDark = T === DARK || dark;
  return (
    <div
      aria-hidden="true"
      role="status"
      aria-label="Carregando tabela..."
      style={{
        background: T?.card || (isDark ? "#1E293B" : "white"),
        borderRadius: RADIUS.lg,
        padding: SP[6],
        border: isDark ? "1px solid rgba(255,255,255,0.06)" : "none",
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: "flex",
          gap: SP[4],
          marginBottom: SP[4],
          paddingBottom: SP[3],
          borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
        }}
      >
        {Array.from({ length: cols }).map((_, i) => (
          <SkeletonBox key={i} h={12} w={i === 0 ? "25%" : "18%"} dark={isDark} />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div key={rowIdx} style={{ display: "flex", gap: SP[4], marginBottom: SP[3] }}>
          {Array.from({ length: cols }).map((_, colIdx) => (
            <SkeletonBox
              key={colIdx}
              h={14}
              w={colIdx === 0 ? "30%" : `${15 + Math.random() * 10}%`}
              dark={isDark}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

interface SkeletonChartProps {
  h?: number;
  dark?: boolean;
  T?: ThemeObject;
}

/**
 * SkeletonChart — placeholder for chart loading states.
 */
export function SkeletonChart({ h = 240, dark = false, T }: SkeletonChartProps) {
  const isDark = T === DARK || dark;
  return (
    <div
      aria-hidden="true"
      role="status"
      aria-label="Carregando gráfico..."
      style={{
        background: T?.card || (isDark ? "#1E293B" : "white"),
        borderRadius: RADIUS.lg,
        padding: SP[6],
        border: isDark ? "1px solid rgba(255,255,255,0.06)" : "none",
      }}
    >
      <SkeletonBox h={16} w="40%" mb={16} dark={isDark} />
      <SkeletonBox h={h} w="100%" dark={isDark} br={RADIUS.sm} />
    </div>
  );
}
